import React, { useState, useEffect, useCallback } from 'react';
import {
  HiOutlinePlay, HiOutlineStop, HiOutlineRefresh, HiOutlineServer,
  HiOutlineExclamationCircle, HiOutlineClock, HiOutlineStatusOnline,
  HiOutlineInformationCircle, HiOutlineCheckCircle, HiOutlineSwitchVertical,
  HiOutlineDatabase, HiOutlineShieldCheck
} from 'react-icons/hi';
import { StagingControlSkeleton } from '../components/Skeleton';
import ConfirmModal from '../components/ConfirmModal';

const API_BASE = process.env.REACT_APP_API_URL || '';
const REFRESH_INTERVAL = 15000;

function formatUptime(ms) {
  if (!ms) return '—';
  const seconds = Math.floor((Date.now() - ms) / 1000);
  const d = Math.floor(seconds / 86400);
  const h = Math.floor((seconds % 86400) / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (d > 0) return `${d}j ${h}h ${m}m`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

function getProgressColor(percent) {
  if (percent < 60) return 'green';
  if (percent < 85) return 'yellow';
  return 'red';
}

export default function StagingControlPage({ token }) {
  const [status, setStatus] = useState(null);
  const [scheduler, setScheduler] = useState(null);
  const [mysql, setMySQL] = useState(null);
  const [ssl, setSSL] = useState(null);
  const [sslRenewStatus, setSSLRenewStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState('');
  const [message, setMessage] = useState(null);
  const [ec2Error, setEc2Error] = useState(null);
  const [confirmModal, setConfirmModal] = useState(null);
  const [showAllProcesses, setShowAllProcesses] = useState(false);

  const BUSINESS_APPS = ['ds5-backend', 'ds5-frontend', 'ds5-suivi'];

  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  const fetchStatus = useCallback(async () => {
    try {
      setEc2Error(null);
      const [statusRes, schedulerRes, mysqlRes, sslRes, sslRenewRes] = await Promise.all([
        fetch(`${API_BASE}/api/staging/status`, { headers }).catch(() => null),
        fetch(`${API_BASE}/api/staging/scheduler/status`, { headers }).catch(() => null),
        fetch(`${API_BASE}/api/staging/mysql`, { headers }).catch(() => null),
        fetch(`${API_BASE}/api/staging/ssl`, { headers }).catch(() => null),
        fetch(`${API_BASE}/api/staging/ssl/renew-status`, { headers }).catch(() => null),
      ]);

      if (statusRes?.ok) {
        setStatus(await statusRes.json());
      } else if (statusRes) {
        const errData = await statusRes.json().catch(() => ({}));
        if (errData.error?.includes('credentials') || errData.error?.includes('Credential') || errData.error?.includes('could not be found')) {
          setEc2Error('Identifiants AWS non configurés. Vérifiez la configuration du serveur.');
        } else {
          setEc2Error(errData.error || 'Erreur de communication avec le serveur staging');
        }
      }

      if (schedulerRes?.ok) setScheduler(await schedulerRes.json());
      if (mysqlRes?.ok) setMySQL(await mysqlRes.json()); else setMySQL(null);
      if (sslRes?.ok) setSSL(await sslRes.json()); else setSSL(null);
      if (sslRenewRes?.ok) setSSLRenewStatus(await sslRenewRes.json()); else setSSLRenewStatus(null);
    } catch (err) {
      console.error('Fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchStatus();
    const interval = setInterval(fetchStatus, REFRESH_INTERVAL);
    return () => clearInterval(interval);
  }, [fetchStatus]);

  const doAction = async (url, method = 'POST', body = null) => {
    setActionLoading(url);
    setMessage(null);
    try {
      const res = await fetch(`${API_BASE}${url}`, {
        method,
        headers,
        body: body ? JSON.stringify(body) : null,
      });
      const data = await res.json();
      setMessage({ type: res.ok ? 'success' : 'error', text: data.message || data.error });
      await fetchStatus();
      if (url.includes('/ec2/')) {
        setTimeout(fetchStatus, 5000);
        setTimeout(fetchStatus, 12000);
      }
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    } finally {
      setActionLoading('');
    }
  };

  if (loading) {
    return <StagingControlSkeleton />;
  }

  const ec2State = status?.ec2?.state || 'unknown';
  const isRunning = ec2State === 'running';
  const isPending = ['pending', 'stopping', 'shutting-down'].includes(ec2State);
  const system = status?.system;

  // Derive overall health from EC2 + PM2 (like prod DashboardPage)
  const overallHealth = (() => {
    if (!isRunning) return 'down';
    if (!status?.pm2?.length) return 'degraded';
    const allOnline = status.pm2.filter(s => BUSINESS_APPS.includes(s.name)).every(s => s.status === 'online');
    return allOnline ? 'healthy' : 'degraded';
  })();

  // Build health cards from PM2 data (like prod's health cards)
  const healthCards = isRunning ? [
    ...BUSINESS_APPS.map(name => {
      const svc = status?.pm2?.find(p => p.name === name);
      return {
        name,
        label: name === 'ds5-backend' ? 'DS5 Backend (API)' : name === 'ds5-frontend' ? 'DS5 Frontend' : 'DS5 Suivi',
        status: svc?.status === 'online' ? 'healthy' : 'down',
        detail: svc?.status === 'online'
          ? `CPU ${svc.cpu}% — RAM ${svc.memory} Mo`
          : svc?.status || 'Non trouvé',
      };
    }),
    {
      name: 'mysql',
      label: 'MySQL',
      status: isRunning ? 'healthy' : 'down',
      detail: isRunning ? 'Base staging active' : 'Serveur arrêté',
      isDb: true,
    },
  ] : [];

  return (
    <div className="fade-in">
      <div className="page-title">
        <HiOutlineStatusOnline className="title-icon" style={{ color: 'var(--accent-yellow)' }} />
        Contrôle Staging
      </div>

      {/* Message banner */}
      {message && (
        <div
          className="card"
          style={{
            marginBottom: 16,
            borderLeft: `4px solid ${message.type === 'success' ? 'var(--accent-green)' : 'var(--accent-red)'}`,
            padding: '12px 20px',
          }}
        >
          {message.text}
        </div>
      )}

      {/* Error banner */}
      {ec2Error && (
        <div className="card" style={{ marginBottom: 16, borderLeft: '4px solid var(--accent-yellow)', padding: '16px 20px' }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
            <HiOutlineExclamationCircle style={{ color: 'var(--accent-yellow)', fontSize: 24, flexShrink: 0, marginTop: 2 }} />
            <div>
              <div style={{ fontWeight: 600, marginBottom: 4 }}>Erreur de connexion</div>
              <div style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.5 }}>{ec2Error}</div>
            </div>
          </div>
        </div>
      )}

      {/* Status Banner (like prod) */}
      <div className={`status-banner ${overallHealth}`}>
        <div className="card-header">
          <div className="card-title">
            <HiOutlineStatusOnline className="icon" /> État du système — Staging
          </div>
          <span className={`badge ${overallHealth}`}>
            <span className="dot"></span>
            {overallHealth === 'healthy' ? 'OPÉRATIONNEL' : overallHealth === 'degraded' ? 'DÉGRADÉ' : 'ARRÊTÉ'}
          </span>
        </div>
        <h2>
          {overallHealth === 'healthy' ? '✓ Tous les services staging fonctionnels'
            : overallHealth === 'degraded' ? '⚠ Attention requise'
            : '⏸ Serveur staging arrêté'}
        </h2>
        {system?.uptime && (
          <div className="subtitle">Uptime : {system.uptime.formatted}</div>
        )}
      </div>

      {/* Health Cards (like prod) */}
      {isRunning && healthCards.length > 0 && (
        <div className="grid-4" style={{ marginBottom: 24 }}>
          {healthCards.map(svc => (
            <div className="card" key={svc.name}>
              <div className="service-card">
                <span className="service-icon">
                  {svc.isDb ? <HiOutlineDatabase /> : <HiOutlineServer />}
                </span>
                <div>
                  <div className="service-name">{svc.label}</div>
                  <div className="service-status" style={{ color: svc.status === 'healthy' ? 'var(--accent-green)' : 'var(--accent-red)' }}>
                    <HiOutlineCheckCircle style={{ verticalAlign: 'middle', marginRight: 4 }} />
                    {svc.status === 'healthy' ? 'HEALTHY' : 'DOWN'}
                  </div>
                  <div className="service-detail">{svc.detail}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Server Status + Actions */}
      <div className="card" style={{ marginBottom: 24 }}>
        <div className="card-header">
          <div className="card-title"><HiOutlineServer className="icon" /> État du serveur</div>
          {!ec2Error && (
            <span className={`badge ${ec2State}`}>
              <span className="dot"></span>
              {ec2State === 'running' ? 'EN LIGNE' : ec2State === 'stopped' ? 'ARRÊTÉ' : ec2State.toUpperCase()}
            </span>
          )}
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 16 }}>
          <div>
            {status?.ec2?.ip && (
              <div style={{ fontSize: 14, color: 'var(--text-muted)' }}>
                Adresse IP : <strong>{status.ec2.ip}</strong>
              </div>
            )}
            {isRunning && system?.uptime && (
              <div style={{ fontSize: 14, color: 'var(--text-muted)', marginTop: 4 }}>
                <HiOutlineClock style={{ verticalAlign: 'middle', marginRight: 4 }} />
                Uptime : <strong style={{ color: 'var(--accent-green)' }}>{system.uptime.formatted}</strong>
              </div>
            )}
          </div>

          <div className="staging-actions">
            <button
              className="btn btn-success"
              disabled={isRunning || isPending || !!actionLoading || !!ec2Error}
              onClick={() => doAction('/api/staging/ec2/start')}
            >
              {actionLoading === '/api/staging/ec2/start' ? <span className="spin">⟳</span> : <HiOutlinePlay />}
              Démarrer
            </button>
            <button
              className="btn btn-danger"
              disabled={!isRunning || isPending || !!actionLoading || !!ec2Error}
              onClick={() => setConfirmModal({
                title: 'Arrêter le serveur staging',
                message: 'Le serveur staging sera complètement arrêté. Toutes les applications seront indisponibles.',
                warning: 'Les utilisateurs connectés au staging perdront leur session.',
                confirmLabel: 'Arrêter',
                danger: true,
                onConfirm: () => { setConfirmModal(null); doAction('/api/staging/ec2/stop'); },
              })}
            >
              {actionLoading === '/api/staging/ec2/stop' ? <span className="spin">⟳</span> : <HiOutlineStop />}
              Arrêter
            </button>
          </div>
        </div>
      </div>

      {/* PM2 Process + Performance */}
      {isRunning && (
        <div className="grid-2-1" style={{ marginBottom: 24 }}>
          {/* PM2 Services with per-app restart */}
          {status?.pm2?.length > 0 && (
            <div className="card">
              <div className="card-header">
                <div className="card-title"><HiOutlineServer className="icon" /> Services applicatifs</div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <button
                    className="btn btn-outline"
                    style={{ padding: '4px 10px', fontSize: 11 }}
                    onClick={() => setShowAllProcesses(v => !v)}
                  >
                    {showAllProcesses ? 'Apps métier uniquement' : 'Afficher tout'}
                  </button>
                  <button
                    className="btn btn-outline"
                    style={{ padding: '6px 12px', fontSize: 12 }}
                    disabled={!!actionLoading}
                    onClick={() => setConfirmModal({
                      title: 'Redémarrer tous les services',
                      message: 'Tous les services applicatifs du staging seront redémarrés simultanément.',
                      warning: 'Les applications seront momentanément indisponibles.',
                      confirmLabel: 'Redémarrer tout',
                      danger: true,
                      onConfirm: () => { setConfirmModal(null); doAction('/api/staging/pm2/restart'); },
                    })}
                  >
                    {actionLoading === '/api/staging/pm2/restart' ? <span className="spin">⟳</span> : <HiOutlineRefresh />}
                    Redémarrer tout
                  </button>
                </div>
              </div>
              <div className="table-container">
                <table>
                  <thead>
                    <tr>
                      <th>Service</th>
                      <th>Version</th>
                      <th>Status</th>
                      <th>CPU</th>
                      <th>RAM</th>
                      <th>Uptime</th>
                      <th>Restarts</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {status.pm2
                      .filter(svc => showAllProcesses || BUSINESS_APPS.includes(svc.name))
                      .map(svc => (
                      <tr key={svc.name}>
                        <td style={{ fontWeight: 500 }}>{svc.name}</td>
                        <td style={{ fontSize: 12, color: 'var(--text-muted)' }}>{svc.version || '—'}</td>
                        <td><span className={`badge ${svc.status}`}>{svc.status}</span></td>
                        <td>{svc.cpu}%</td>
                        <td>{svc.memory} Mo</td>
                        <td><HiOutlineClock style={{ verticalAlign: 'middle', marginRight: 4 }} />{formatUptime(svc.uptime)}</td>
                        <td>{svc.restarts}</td>
                        <td>
                          <button
                            className="btn-icon"
                            title={`Redémarrer ${svc.name}`}
                            disabled={!!actionLoading}
                            onClick={() => setConfirmModal({
                              title: `Redémarrer ${svc.name}`,
                              message: `Voulez-vous redémarrer le service "${svc.name}" sur le staging ?`,
                              confirmLabel: 'Redémarrer',
                              danger: false,
                              onConfirm: () => { setConfirmModal(null); doAction(`/api/staging/pm2/restart/${svc.name}`); },
                            })}
                          >
                            <HiOutlineRefresh className={actionLoading === `/api/staging/pm2/restart/${svc.name}` ? 'spin' : ''} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          {/* Performance card — like prod */}
          {system && (
            <div className="card">
              <div className="card-header">
                <div className="card-title"><HiOutlineCheckCircle className="icon" /> Performance</div>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
                {/* CPU */}
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span className="metric-label">CPU</span>
                    <span style={{ fontWeight: 600 }}>{system.cpu?.percent ?? 0}%</span>
                  </div>
                  <div className="progress-bar">
                    <div
                      className={`progress-fill ${getProgressColor(system.cpu?.percent ?? 0)}`}
                      style={{ width: `${Math.min(system.cpu?.percent ?? 0, 100)}%` }}
                    />
                  </div>
                </div>

                {/* RAM */}
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span className="metric-label">Mémoire</span>
                    <span style={{ fontWeight: 600 }}>{system.memory?.percent ?? 0}%</span>
                  </div>
                  <div className="progress-bar">
                    <div
                      className={`progress-fill ${getProgressColor(system.memory?.percent ?? 0)}`}
                      style={{ width: `${system.memory?.percent ?? 0}%` }}
                    />
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>
                    {system.memory?.usedMB} / {system.memory?.totalMB} Mo
                  </div>
                </div>

                {/* Disk */}
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span className="metric-label">Disque</span>
                    <span style={{ fontWeight: 600 }}>{system.disk?.percent ?? 0}%</span>
                  </div>
                  <div className="progress-bar">
                    <div
                      className={`progress-fill ${getProgressColor(system.disk?.percent ?? 0)}`}
                      style={{ width: `${system.disk?.percent ?? 0}%` }}
                    />
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>
                    {Math.round((system.disk?.usedMB || 0) / 1024)} / {Math.round((system.disk?.totalMB || 0) / 1024)} Go
                  </div>
                </div>

                {/* Uptime */}
                <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: 8, borderTop: '1px solid var(--border-color)' }}>
                  <span className="metric-label">Uptime serveur</span>
                  <span style={{ fontWeight: 600, color: 'var(--accent-green)' }}>{system.uptime?.formatted}</span>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* MySQL + SSL — only when EC2 is running */}
      {isRunning && (
        <div className="grid-2" style={{ marginBottom: 24 }}>
          {/* MySQL */}
          <div className="card">
            <div className="card-header">
              <div className="card-title"><HiOutlineServer className="icon" /> MySQL</div>
              <span className={`badge ${mysql?.connected ? 'healthy' : 'down'}`}>
                {mysql?.connected ? 'CONNECTÉ' : 'DOWN'}
              </span>
            </div>
            {mysql?.connected ? (
              <table>
                <tbody>
                  <tr><td className="metric-label">Taille DB</td><td style={{ fontWeight: 600 }}>{(mysql.dbSizeMB / 1024).toFixed(2)} Go</td></tr>
                  <tr><td className="metric-label">Threads actifs</td><td style={{ fontWeight: 600 }}>{mysql.threadsConnected}</td></tr>
                  <tr><td className="metric-label">Processus actifs</td><td style={{ fontWeight: 600 }}>{mysql.activeProcesses}</td></tr>
                  <tr><td className="metric-label">Requêtes totales</td><td style={{ fontWeight: 600 }}>{mysql.totalQueries?.toLocaleString()}</td></tr>
                  <tr><td className="metric-label">Requêtes lentes</td><td style={{ fontWeight: 600, color: mysql.slowQueries > 0 ? 'var(--accent-yellow)' : 'inherit' }}>{mysql.slowQueries}</td></tr>
                  <tr><td className="metric-label">Uptime</td><td style={{ fontWeight: 600 }}>{Math.floor(mysql.uptime / 86400)}j</td></tr>
                </tbody>
              </table>
            ) : (
              <div style={{ fontSize: 13, color: 'var(--text-muted)', padding: '12px 0' }}>
                {mysql?.error || 'Données MySQL indisponibles'}
              </div>
            )}
          </div>

          {/* SSL Certificates */}
          <div className="card">
            <div className="card-header">
              <div className="card-title"><HiOutlineShieldCheck className="icon" /> Certificats SSL</div>
              <button
                className="btn btn-outline"
                style={{ padding: '4px 10px', fontSize: 12 }}
                disabled={!!actionLoading}
                onClick={() => setConfirmModal({
                  title: 'Renouveler les certificats SSL',
                  message: 'Lance `certbot renew` sur le serveur staging. Les certificats dont l\'expiration est < 30 jours seront renouvelés.',
                  confirmLabel: 'Renouveler',
                  onConfirm: () => { setConfirmModal(null); doAction('/api/staging/ssl/renew'); },
                })}
              >
                {actionLoading === '/api/staging/ssl/renew' ? <span className="spin">⟳</span> : <HiOutlineRefresh />}
                Renouveler
              </button>
            </div>

            {/* Last renew attempt info */}
            {sslRenewStatus?.available && sslRenewStatus.timestamp && (
              <div style={{
                padding: '8px 12px',
                marginBottom: 8,
                borderRadius: 6,
                background: 'var(--bg-tertiary)',
                fontSize: 12,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}>
                <span className="metric-label">
                  Dernier renew : <strong style={{ color: 'var(--text-primary)' }}>{new Date(sslRenewStatus.timestamp).toLocaleString('fr-FR')}</strong>
                  {sslRenewStatus.trigger && <> · <em>{sslRenewStatus.trigger}</em></>}
                </span>
                <span className={`badge ${sslRenewStatus.success ? 'healthy' : 'down'}`}>
                  {sslRenewStatus.success ? '✓ OK' : `✗ exit=${sslRenewStatus.exitCode}`}
                </span>
              </div>
            )}
            {sslRenewStatus && !sslRenewStatus.available && (
              <div style={{ fontSize: 12, color: 'var(--accent-yellow)', marginBottom: 8 }}>
                ⚠ Auto-renew non configuré ({sslRenewStatus.message || 'migration 002 requise'})
              </div>
            )}

            {ssl?.certificates?.length ? ssl.certificates.map(cert => (
              <div key={cert.name} style={{ padding: '12px 0', borderBottom: '1px solid var(--border-color)' }}>
                <div style={{ fontWeight: 500, marginBottom: 4 }}>{cert.name}</div>
                <div style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 4 }}>{cert.domains}</div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                  <span className="metric-label">Expire : {cert.expiryDate}</span>
                  <span className={`badge ${cert.daysRemaining > 30 ? 'healthy' : cert.daysRemaining > 7 ? 'degraded' : 'down'}`}>
                    {cert.daysRemaining}j
                  </span>
                </div>
              </div>
            )) : (
              <div style={{ fontSize: 13, color: 'var(--text-muted)', padding: '12px 0' }}>
                Aucun certificat détecté
              </div>
            )}
          </div>
        </div>
      )}

      {/* Not running notice */}
      {!isRunning && !ec2Error && (
        <div className="card" style={{ marginBottom: 24 }}>
          <div className="empty-state">
            <div className="empty-icon">⏸️</div>
            <div>Le serveur staging est arrêté.</div>
            <div style={{ fontSize: 13, marginTop: 8 }}>Cliquez sur <strong>Démarrer</strong> pour lancer le serveur.</div>
          </div>
        </div>
      )}

      {/* ─── Scheduler: Programmation horaire ─── */}
      <div className="card" style={{ marginBottom: 24 }}>
        <div className="card-header">
          <div className="card-title"><HiOutlineClock className="icon" /> Programmation horaire</div>
        </div>

        {/* Auto-start */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 0', borderBottom: '1px solid var(--border-color)' }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 4, display: 'flex', alignItems: 'center', gap: 8 }}>
              <HiOutlinePlay style={{ color: 'var(--accent-green)' }} /> Démarrage programmé
            </div>
            <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>
              {scheduler?.autoStart?.enabled
                ? <>Lun-Ven à <strong>{scheduler.autoStart.nextLabel?.split(' (')[0]}</strong> (heure Paris)</>
                : 'Désactivé'}
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            {scheduler?.autoStart?.enabled && (
              <input
                type="time"
                value={`${String(scheduler?.autoStart?.hour ?? 7).padStart(2, '0')}:${String(scheduler?.autoStart?.minute ?? 30).padStart(2, '0')}`}
                onChange={(e) => {
                  const [h, m] = e.target.value.split(':').map(Number);
                  doAction('/api/staging/scheduler/auto-start', 'POST', { enabled: true, hour: h, minute: m });
                }}
                style={{
                  padding: '4px 8px', fontSize: 13, borderRadius: 6,
                  border: '1px solid var(--border-color)', background: 'var(--bg-tertiary)',
                  color: 'var(--text-primary)',
                }}
              />
            )}
            <div
              className={`toggle ${scheduler?.autoStart?.enabled ? 'active' : ''}`}
              onClick={() => {
                if (actionLoading) return;
                const newState = !scheduler?.autoStart?.enabled;
                doAction('/api/staging/scheduler/auto-start', 'POST', {
                  enabled: newState,
                  hour: scheduler?.autoStart?.hour ?? 7,
                  minute: scheduler?.autoStart?.minute ?? 30,
                });
              }}
              style={{ cursor: actionLoading ? 'wait' : 'pointer' }}
            />
          </div>
        </div>

        {/* Auto-stop */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 0', borderBottom: '1px solid var(--border-color)' }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 4, display: 'flex', alignItems: 'center', gap: 8 }}>
              <HiOutlineStop style={{ color: 'var(--accent-red)' }} /> Arrêt programmé
            </div>
            <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>
              {scheduler?.autoStop?.enabled
                ? <>Lun-Ven à <strong>{scheduler.autoStop.nextLabel?.split(' (')[0]}</strong> (heure Paris)</>
                : 'Désactivé'}
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            {scheduler?.autoStop?.enabled && (
              <input
                type="time"
                value={`${String(scheduler?.autoStop?.hour ?? 18).padStart(2, '0')}:${String(scheduler?.autoStop?.minute ?? 30).padStart(2, '0')}`}
                onChange={(e) => {
                  const [h, m] = e.target.value.split(':').map(Number);
                  doAction('/api/staging/scheduler/auto-stop', 'POST', { enabled: true, hour: h, minute: m });
                }}
                style={{
                  padding: '4px 8px', fontSize: 13, borderRadius: 6,
                  border: '1px solid var(--border-color)', background: 'var(--bg-tertiary)',
                  color: 'var(--text-primary)',
                }}
              />
            )}
            <div
              className={`toggle ${scheduler?.autoStop?.enabled ? 'active' : ''}`}
              onClick={() => {
                if (actionLoading) return;
                const newState = !scheduler?.autoStop?.enabled;
                doAction('/api/staging/scheduler/auto-stop', 'POST', {
                  enabled: newState,
                  hour: scheduler?.autoStop?.hour ?? 18,
                  minute: scheduler?.autoStop?.minute ?? 30,
                });
              }}
              style={{ cursor: actionLoading ? 'wait' : 'pointer' }}
            />
          </div>
        </div>

        {/* Sync prod → staging */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 0' }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 4, display: 'flex', alignItems: 'center', gap: 8 }}>
              <HiOutlineSwitchVertical style={{ color: 'var(--accent-blue)' }} /> Synchro Prod → Staging
            </div>
            <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>
              {scheduler?.sync?.enabled
                ? <>Tous les jours à <strong>{scheduler.sync.nextLabel?.split(' (')[0]}</strong> UTC</>
                : 'Désactivée'}
              {scheduler?.sync?.inProgress && <span className="badge degraded" style={{ marginLeft: 8, fontSize: 10 }}>En cours...</span>}
            </div>
            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
              Utilise le dernier backup prod (cron backup à 02:00 UTC)
              {scheduler?.sync?.lastResult && (
                <> — Dernier résultat : <span style={{ color: scheduler.sync.lastResult.status === 'success' ? 'var(--accent-green)' : 'var(--accent-red)' }}>
                  {scheduler.sync.lastResult.status === 'success' ? '✓ OK' : '✗ Échec'}
                </span> ({scheduler.sync.lastResult.duration}s)</>
              )}
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {scheduler?.sync?.enabled && (
              <button
                className="btn btn-outline"
                style={{ padding: '4px 10px', fontSize: 11 }}
                disabled={!!actionLoading || scheduler?.sync?.inProgress}
                onClick={() => setConfirmModal({
                  title: 'Lancer la synchro maintenant',
                  message: 'Le dernier backup prod sera restauré sur staging. Si le staging est arrêté, il sera démarré automatiquement puis ré-arrêté.',
                  warning: 'Les données staging actuelles seront écrasées par celles de production.',
                  confirmLabel: 'Synchroniser',
                  danger: true,
                  onConfirm: () => { setConfirmModal(null); doAction('/api/staging/scheduler/sync/run'); },
                })}
              >
                {scheduler?.sync?.inProgress ? <span className="spin">⟳</span> : '▶'} Sync maintenant
              </button>
            )}
            <div
              className={`toggle ${scheduler?.sync?.enabled ? 'active' : ''}`}
              onClick={() => {
                if (actionLoading) return;
                const newState = !scheduler?.sync?.enabled;
                if (newState) {
                  setConfirmModal({
                    title: 'Activer la synchro Prod → Staging',
                    message: `La synchro restaurera le dernier backup prod sur staging tous les jours à ${String(scheduler?.sync?.hour ?? 3).padStart(2, '0')}:${String(scheduler?.sync?.minute ?? 0).padStart(2, '0')} UTC.`,
                    warning: 'Les données staging seront écrasées quotidiennement. Si le staging est arrêté, il sera démarré pour la synchro puis ré-arrêté.',
                    confirmLabel: 'Activer',
                    danger: true,
                    onConfirm: () => {
                      setConfirmModal(null);
                      doAction('/api/staging/scheduler/sync', 'POST', { enabled: true });
                    },
                  });
                } else {
                  doAction('/api/staging/scheduler/sync', 'POST', { enabled: false });
                }
              }}
              style={{ cursor: actionLoading ? 'wait' : 'pointer' }}
            />
          </div>
        </div>
      </div>

      {/* Technical Details Footer */}
      <div className="card staging-tech-footer">
        <div className="card-header" style={{ borderBottom: 'none', paddingBottom: 0 }}>
          <div className="card-title" style={{ fontSize: 12, color: 'var(--text-muted)' }}>
            <HiOutlineInformationCircle className="icon" /> Détails techniques
          </div>
        </div>
        <div className="tech-details-grid">
          <span>Plateforme : <strong>AWS EC2</strong></span>
          <span>Type : <strong>{status?.ec2?.instanceType || 't3.medium'}</strong></span>
          <span>Région : <strong>eu-west-2 (London)</strong></span>
          <span>Instance : <strong>{status?.ec2?.instanceId || 'i-02ddda5ba0d4d9ede'}</strong></span>
          {system?.nodeVersion && <span>Node.js : <strong>{system.nodeVersion}</strong></span>}
          {system?.osVersion && <span>OS : <strong>{system.osVersion}</strong></span>}
        </div>
      </div>

      {/* Confirm modal */}
      <ConfirmModal
        open={!!confirmModal}
        title={confirmModal?.title}
        message={confirmModal?.message}
        warning={confirmModal?.warning}
        confirmLabel={confirmModal?.confirmLabel}
        danger={confirmModal?.danger}
        onConfirm={confirmModal?.onConfirm}
        onCancel={() => setConfirmModal(null)}
      />
    </div>
  );
}
